import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    HierarchyEntity,
    HierarchyWebModel,
    HierarchyLevel,
    HierarchyEntityShort,
    HierarchyDetailsModel as HierarchyCompleteModel,
    HierarchyDetailsModel,
    HierarchyType,
    UserHierarchyByRole,
} from '../interfaces/hierarchy-data';
import {
    ClientHierarchy,
    HierarchyRoleType,
    ClientHierarchyMain,
} from '../../shared/interfaces/user-location';

@Injectable({
    providedIn: 'root',
})
export class HierarchyService {
    constructor(private http: HttpClient) {}

    private serverUrl = 'https://' + window.location.host;
    private version = '0.5'; // TODO Get version number dynamically
    private _url = (this.serverUrl + '/api/v{version}/hierarchy').replace(
        '{version}',
        this.version
    );

    getHierarchyEntities(): Observable<HierarchyEntity[]> {
        return this.http.get<HierarchyEntity[]>(this._url + '/GetHierarchyEntities/');
    }

    getHierarchyEntitiesForClient(clientKey: number): Observable<HierarchyEntity[]> {
        const params = {
            clientKey: clientKey.toString(),
        };
        return this.http.get<HierarchyEntity[]>(this._url + '/GetHierarchyEntitiesForClient/', {
            params: params,
        });
    }

    getHierarchyLevelsOfInterest(): Observable<HierarchyLevel[]> {
        return this.http.get<HierarchyLevel[]>(this._url + '/GetHierarchyLevelsOfInterest');
    }

    getHierarchyEntitiesOfInterest(): Observable<HierarchyEntityShort[]> {
        return this.http.get<HierarchyEntityShort[]>(this._url + '/GetHierarchyEntitiesOfInterest');
    }

    getHierarchyDetailsModel(id: string): Observable<HierarchyDetailsModel> {
        return this.http.post<HierarchyDetailsModel>(this._url + '/GetHierarchyCompleteModel', {
            identifier: id,
        });
    }

    upsertNewHierarchyEntity(formModel: any, origOwner: string) {
        this.cleanUpData(formModel);
        formModel.originalOwnerIdentifier = origOwner;
        return new Observable((subscriber) => {
            this.http.post<any>(`${this._url}/UpsertNewHierarchyEntity`, formModel).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }
    private cleanUpData(formModel: any) {
        if (!formModel.generalInfo.hierarchyKey) {
            formModel.generalInfo.hierarchyKey = null;
        }
        formModel.hierarchyInfo.roleOrder = +formModel.hierarchyInfo.roleOrder;
        if (!formModel.hierarchyInfo.parentHierarchyKey) {
            formModel.hierarchyInfo.parentHierarchyKey = null;
        }
        formModel.generalInfo.hierarchyTypeKey = +formModel.generalInfo.hierarchyTypeKey;
        if (
            !formModel.generalInfo.ownerIdentifier &&
            formModel.generalInfo.selectOwner &&
            formModel.generalInfo.selectOwner.length
        ) {
            formModel.generalInfo.ownerIdentifier =
                formModel.generalInfo.selectOwner[0].primaryIdentifier;
        }
        if (
            !formModel.hierarchyInfo.parentHierarchyKey &&
            formModel.hierarchyInfo.parentHierarchyKey &&
            formModel.hierarchyInfo.parentHierarchyKey.length
        ) {
            formModel.hierarchyInfo.parentHierarchyKey =
                formModel.hierarchyInfo.parentHierarchyKey[0].hierarchyKey;
        }
        if (
            formModel.generalInfo.locationInfo &&
            formModel.generalInfo.locationInfo.zipCodeDeliverySpecific
        ) {
            formModel.generalInfo.locationInfo.zipCode =
                formModel.generalInfo.locationInfo.zipCode +
                formModel.generalInfo.locationInfo.zipCodeDeliverySpecific;
        }
    }

    getCorporateAllHierarchy() {
        return this.http.get<ClientHierarchy[]>(this._url + '/CorporateAll');
    }

    getHierarchyRoleTypes() {
        return this.http.get<HierarchyRoleType[]>(this._url + '/RoleTypes');
    }

    getHierarchyTypes() {
        return this.http.get<HierarchyType[]>(this._url + '/GetHierarchyTypes');
    }

    updateHierarchyLevels(hierarchyData: ClientHierarchyMain[]) {
        const params = new HttpParams().set('hierarchyData', JSON.stringify(hierarchyData));

        return this.http.get<number>(this._url + '/UpdateHierarchyLevels', {
            params: params,
        });
    }

    deleteHierarchyLevels(hierarchyData: ClientHierarchy) {
        const params = new HttpParams().set('roleOrder', hierarchyData.roleOrder.toString());

        return this.http.get<number>(this._url + '/DeleteHierarchyLevels', {
            params: params,
        });
    }

    getBranches(): Observable<HierarchyWebModel[]> {
        return this.http.get<HierarchyWebModel[]>(this._url + '/branches/all');
    }

    getHierarchyByRole(roleName: string): Observable<UserHierarchyByRole[]> {
        const params = new HttpParams().set('roleName', roleName);

        return this.http.get<UserHierarchyByRole[]>(this._url + '/DisplayUserHierarchyByRole', {
            params: params,
        });
    }

    getEntitiesByRoleOrder(roleOrder: number): Observable<HierarchyWebModel[]> {
        const params = new HttpParams().set('roleOrder', roleOrder.toString());
        return this.http.get<HierarchyWebModel[]>(this._url + '/level', {
            params: params,
        });
    }

    getEmployeeListByAsOfDateAndPosition(parameters: object = null) {
        let params: HttpParams = new HttpParams().set('paramsString', JSON.stringify(parameters));
        return this.http.get<UserHierarchyByRole[]>(this._url + '/GetEmployeeListByPosition', {
            params: params,
        });
    }

    doHierarchyEntitiesExistForClient(clientShortName: string = ''): Observable<boolean> {
        const params = new HttpParams().set('clientShortName', clientShortName);
        return this.http.get<boolean>(this._url + '/DoHierarchyEntitiesExistForClient', {
            params: params,
        });
    }
}
